import { GenericModal } from '@/components/shared/GenericModal';
import { Button, Input, Stack, Typography } from '@mui/material';
import {
  ActionButton,
  FooterText,
  SectionTitle,
  SelectionDialogActions,
  SelectionDialogContent,
  crossIconStyles,
  headerStyles,
  modalStyles,
  paperStyles,
} from './styles';
import { useRef, useState } from 'react';
import { FileUploadOutlined } from '@mui/icons-material';
import { OptionBox, TemplateFieldContainer } from '../styles';
import { DisplayColor } from '@/__generated__/graphql';
import { ColorSquare } from '../../../Analysis/DeckPlan/ColorLegend/ColorLegend';
import { abyssColors } from '@/theme/colors';

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  handleUpdate?: (argument0: string[]) => void;
  handleUpdateWithColors?: (argument0: string[], argument1: DisplayColor[]) => void;
  enableColors?: boolean;
};

type FileError = { title: string; description: string };

const fileErrors = {
  typeError: {
    title: 'Incorrect File format',
    description: 'You have uploaded the incorrect file type. Please upload a .xlsx file.',
  },
  sizeError: {
    title: 'File size exceeded',
    description:
      'The file size exceeds the limit of 50MB. Please upload a file with a size less than 50MB.',
  },
};

export const BulkOptionsModal = ({
  open,
  handleClose,
  handleUpdate = () => {},
  handleUpdateWithColors = () => {},
  enableColors = false,
}: ModalProps) => {
  const fileInputReference = useRef<HTMLInputElement | undefined>(undefined);
  const [fileError, setFileError] = useState<FileError | undefined>(undefined);
  const [fileOptions, setFileOptions] = useState<string[]>([]);
  const [fileOptionColors, setFileOptionColors] = useState<DisplayColor[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const isCSV = file.name.endsWith('.csv');
    const fileSize = file.size / (1024 * 1024);
    const isError = !isCSV || fileSize > 50;
    if (isError) {
      setFileError(isCSV ? fileErrors.sizeError : fileErrors.typeError);
      return;
    }
    setFileError(undefined);
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const base64Data = reader.result?.toString().split(',')[1] ?? '';
      const csvData = atob(base64Data);
      const rows = csvData.split('\n').map((row) => row.split(','));

      const options = rows.map((row) => row[0].trim()).filter(Boolean);
      setFileOptions(options);

      if (enableColors) {
        const colors = rows
          .map((row) => ({
            key: row[0]?.trim(),
            value: row[1]?.trim(),
          }))
          .filter((item) => item.key && item.value);
        setFileOptionColors(colors);
      }
    });

    reader.readAsDataURL(file);
    const fileInput = event.target;
    fileInput.value = '';
  };

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="Adding more options"
      sx={modalStyles}
      backgroundPaperStyle={paperStyles}
      headerStyleProp={headerStyles}
      crossIconStyle={crossIconStyles}
    >
      <SelectionDialogContent>
        <Stack gap={2}>
          <Button
            variant="outlined"
            size="small"
            sx={{ width: 'fit-content', height: '32px' }}
            startIcon={<FileUploadOutlined />}
            onClick={() => fileInputReference.current?.click()}
          >
            Upload File
          </Button>
          <TemplateFieldContainer sx={{ height: '24rem' }}>
            {fileOptions.map((option, index) => (
              <OptionBox key={`${option}-${index}`}>
                <Typography>{option}</Typography>
                <Stack direction="row" gap={2}>
                  {enableColors && fileOptionColors && (
                    <ColorSquare
                      sx={{
                        width: '1.6rem',
                        height: '1.6rem',
                        border: `1px solid ${abyssColors.neutral[300]}`,
                      }}
                      color={
                        fileOptionColors.find((color) => color?.key === option)?.value ??
                        'transparent'
                      }
                    />
                  )}
                </Stack>
              </OptionBox>
            ))}
          </TemplateFieldContainer>
        </Stack>
        {fileError && (
          <>
            <SectionTitle sx={{ marginTop: '16px', fontSize: '14px', fontWeight: 600 }}>
              {fileError?.title}
            </SectionTitle>
            <SectionTitle>{fileError?.description}</SectionTitle>
          </>
        )}
        <Input
          inputRef={fileInputReference}
          hidden
          type="file"
          id="upload-file"
          style={{ display: 'none' }}
          inputProps={{ accept: '.csv' }}
          onChange={handleFileSelect}
        />
      </SelectionDialogContent>
      <SelectionDialogActions>
        <FooterText>{fileOptions.length} Uploaded</FooterText>
        <Stack direction="row" gap={2}>
          <ActionButton onClick={handleClose}>
            <span>Cancel</span>
          </ActionButton>
          <ActionButton
            variant="contained"
            contained
            disabled={!!fileError || fileOptions.length === 0}
            onClick={() => {
              if (enableColors) {
                handleUpdateWithColors(fileOptions, fileOptionColors);
              } else {
                handleUpdate(fileOptions);
              }
              handleClose();
            }}
          >
            Add
          </ActionButton>
        </Stack>
      </SelectionDialogActions>
    </GenericModal>
  );
};
